// --- Core ---
import {PercentCrop} from "react-image-crop";
import {last} from "underscore";
// --- SaniQ ---
import {
  DeserializedRecord,
  deserializeItem,
  Deserializer,
  deserializeSingleRelationship,
  JSONAPIRecord,
  JSONAPISingleRelationship
} from "../../Helpers";
import {deserializeGroup, Group} from "../Group";
import {PictureKey} from "vendor/qurasoft/definitions/types/measurements";

const POSSIBLE_VERSIONS = ["0.1", "0.2"] as const;
type PossibleVersions = typeof POSSIBLE_VERSIONS[number];

const CURRENT_PROCESSING_VERSION = last(POSSIBLE_VERSIONS);

interface PictureProcessing {
  crop: PercentCrop;
  rotation: number;
  //The processing is stored as JSON in the API.
  //So in case the crop library is changed or something similar, this can be used to determine necessary conversions
  version: PossibleVersions;
}

interface Picture extends DeserializedRecord<"picture"> {
  group: Group;
  pictureType: PictureKey;
  processing: PictureProcessing;
  // An old processing version was used and some of the information could not be converted
  staleProcessing: boolean;
}

interface PictureData extends JSONAPIRecord {
  attributes: {
    processing?: PictureProcessing
  } & JSONAPIRecord["attributes"]
  relationships: {
    patient_measurement_group: JSONAPISingleRelationship;
  }
}

const deserializePicture: Deserializer<Picture, PictureData> = (data, included) => {
  const group = deserializeSingleRelationship(data.relationships.patient_measurement_group, included, deserializeGroup);
  const {processing} = data.attributes;
  let parsedProcessing = processing;
  let staleProcessing = false;

  if (processing) {
    //NOTE(Keune): As long as the processing version does not match, upgrade it
    while (processing.version !== CURRENT_PROCESSING_VERSION) {
      if (processing.version === "0.1") {
        //NOTE(Keune): In 0.1 crop was PixelCrop and in 0.2 it is PercentCrop. This cannot be fixed without the image dimensions
        parsedProcessing.crop = undefined;
        parsedProcessing.version = "0.2";
        staleProcessing = true;
      } else {
        parsedProcessing = undefined;
        // eslint-disable-next-line no-console
        console.error(`Not supported picture processing version '${processing.version}' for record ${data.id}`);
        //NOTE(Keune): As soon as one version is not supported, break out of the loop. Otherwise it would be infinite
        break;
      }
    }
  }

  return {
    ...deserializeItem(data),
    group,
    processing: parsedProcessing,
    staleProcessing
  };
};

export {
  CURRENT_PROCESSING_VERSION,
  deserializePicture,
  Picture,
  PictureProcessing
};
